/**
 * Modulation Lab v1 — pure analyzer for cross-key modulation training.
 *
 * Two sub-modes: Modulation Explore (free-explore source × technique ×
 * destination) and Modulation ID Quiz (10-Q recognition session). Four
 * canonical techniques: direct (chromatic), pivot (shared diatonic chord),
 * common tone (held pitch through harmonic shift), parallel mode (major ↔
 * minor on same tonic).
 *
 * v1 source pool: 12 majors (flat-side enharmonics — Db not C#). v1
 * destinations: closely-related cluster (5 keys per source via
 * cof-wheel.getCloselyRelatedKeys) + parallel minor for the parallel
 * technique.
 *
 * Spec: docs/superpowers/specs/2026-05-26-modulation-lab-v1-design.md §4
 * Bowie scope memo: .claude/agent-memory/bowie/project_modulation_lab_v1_scope.md
 */

import type { NoteName } from '@tunasong/schemas'
import { ChordLib, type Chord, type ChordVariant } from '../chord/index.js'
import ScaleLib from '../scale/scale.js'
import { getCloselyRelatedKeys } from './cof-wheel.js'

export const SOURCE_KEY_POOL = [
  'C',
  'G',
  'D',
  'A',
  'E',
  'B',
  'F#',
  'Db',
  'Ab',
  'Eb',
  'Bb',
  'F',
] as const

export type SourceKey = (typeof SOURCE_KEY_POOL)[number]

export const MODULATION_TECHNIQUES = ['direct', 'pivot', 'common-tone', 'parallel'] as const
export type ModulationTechnique = (typeof MODULATION_TECHNIQUES)[number]

export const getValidDestinations = (
  source: SourceKey,
  technique: ModulationTechnique
): readonly string[] => {
  if (technique === 'parallel') {
    return [`${source}m`]
  }
  return getCloselyRelatedKeys(source)
}

// ─── Diatonic chord lookup ───────────────────────────────────────────────────

export interface DiatonicChord {
  /** Chord name as commonly spelled (e.g. 'C', 'Dm', 'Bdim', 'Am'). */
  name: string
  degree: 1 | 2 | 3 | 4 | 5 | 6 | 7
  quality: 'maj' | 'min' | 'dim'
}

/** Triad quality on each major-scale degree. */
const MAJOR_DIATONIC_QUALITIES = ['maj', 'min', 'min', 'maj', 'maj', 'min', 'dim'] as const

/** Triad quality on each natural-minor-scale degree (rotation of major by 5). */
const MINOR_DIATONIC_QUALITIES = ['min', 'dim', 'maj', 'min', 'min', 'maj', 'maj'] as const

const isMinorKey = (key: string): boolean => key.endsWith('m') && !key.endsWith('dim')
const stripMinor = (key: string): string => key.replace(/m$/, '')

const qualitySuffix = (q: 'maj' | 'min' | 'dim'): string => {
  if (q === 'maj') {return ''}
  if (q === 'min') {return 'm'}
  return 'dim'
}

/**
 * Compute the seven diatonic triads for a key. Spelling is delegated to
 * `ScaleLib.getNotes` so the chord roots inherit the scale's enharmonic
 * decisions — keys with flats in their tonic (Bb, Eb, Ab, Db, F, plus minor
 * tonics from the relative-minor cluster) keep flat-side spellings; sharp
 * keys keep sharp-side spellings.
 */
export const getDiatonicChordsForKey = (key: string): DiatonicChord[] => {
  const minor = isMinorKey(key)
  const tonicName = (minor ? stripMinor(key) : key) as NoteName
  const qualities = minor ? MINOR_DIATONIC_QUALITIES : MAJOR_DIATONIC_QUALITIES
  const scaleNotes = ScaleLib.getNotes({
    root: tonicName,
    type: minor ? 'minor' : 'major',
  })
  return scaleNotes.slice(0, 7).map((root, i) => {
    const quality = qualities[i]
    return {
      name: `${root}${qualitySuffix(quality)}`,
      degree: (i + 1) as DiatonicChord['degree'],
      quality,
    }
  })
}

// ─── Pivot chord selection ───────────────────────────────────────────────────

export interface PivotChord {
  name: string
  sourceFunction: { degree: number; quality: 'maj' | 'min' | 'dim' }
  destinationFunction: { degree: number; quality: 'maj' | 'min' | 'dim' }
  functionDistance: number
}

const functionDistance = (a: number, b: number): number => {
  // Scale-degree distance with wraparound (degrees 1..7).
  const direct = Math.abs(a - b)
  const wrap = 7 - direct
  return Math.min(direct, wrap)
}

/**
 * Bowie-locked pivot table for the 5 C-source rows published in the scope
 * memo. The memo's C-source row is the canonical training example.
 *
 * For source/destination pairs NOT in this table, the algorithmic default
 * applies (see comments below). Notably, G→C falls through to the default,
 * which selects pivot = C (IV → I) — the standard pivot-to-IV pattern.
 *
 * Locked verbatim — do not edit without Bowie review.
 */
const BOWIE_LOCKED_PIVOT_TABLE: Record<string, string> = {
  'C->G': 'C',
  'C->F': 'F',
  'C->Am': 'Am',
  'C->Dm': 'Dm',
  'C->Em': 'Em',
}

export const selectPivotChord = (source: string, destination: string): PivotChord | null => {
  const sourceChords = getDiatonicChordsForKey(source)
  const destinationChords = getDiatonicChordsForKey(destination)
  const sourceByName = new Map(sourceChords.map(c => [c.name, c]))

  // Shared = chords present in both keys' diatonic sets (by chord name).
  const shared: PivotChord[] = []
  for (const destChord of destinationChords) {
    const sourceChord = sourceByName.get(destChord.name)
    if (!sourceChord) {continue}
    shared.push({
      name: destChord.name,
      sourceFunction: { degree: sourceChord.degree, quality: sourceChord.quality },
      destinationFunction: { degree: destChord.degree, quality: destChord.quality },
      functionDistance: functionDistance(sourceChord.degree, destChord.degree),
    })
  }
  if (shared.length === 0) {return null}

  const findByName = (name: string): PivotChord | undefined => shared.find(s => s.name === name)

  // 1. Bowie-locked table override — covers the 6 published rows verbatim.
  const lockedPivot = BOWIE_LOCKED_PIVOT_TABLE[`${source}->${destination}`]
  if (lockedPivot) {
    const match = findByName(lockedPivot)
    if (match) {return match}
  }

  // 2. Algorithmic default for un-locked (source, destination) pairs. The
  // pattern observed in Bowie's C-source row is:
  //
  //   - destination = V/source (major destination)  → pivot = I/source
  //   - destination = IV/source (major destination) → pivot = I/destination
  //   - destination is the relative minor of source's IV / V / I
  //     (i.e., vi/ii/iii of source)                 → pivot = I/destination
  //
  // This is the "default rule" applied to the 11 non-C source keys.
  const sourceTonicName = sourceChords[0].name
  const destinationTonicName = destinationChords[0].name
  const destAsSourceDegree = sourceByName.get(destinationTonicName)?.degree
  const sourceTonicAsPivot = findByName(sourceTonicName)
  const destinationTonicAsPivot = findByName(destinationTonicName)

  if (destAsSourceDegree === 5 && sourceTonicAsPivot) {
    return sourceTonicAsPivot
  }
  if (destinationTonicAsPivot) {
    return destinationTonicAsPivot
  }
  // Pathological fallback — shouldn't reach this for valid closely-related
  // destinations, but fall through to the first shared chord (deterministic)
  // rather than throwing.
  return shared[0]
}

// ─── Dominant + common-tone helpers ──────────────────────────────────────────

export interface DominantChord {
  name: string
  root: string
}

export const getDominantOfKey = (destinationKey: string): DominantChord => {
  const minor = isMinorKey(destinationKey)
  const tonicName = (minor ? stripMinor(destinationKey) : destinationKey) as NoteName
  // V root = the 5th scale degree of the destination key (perfect 5th above the
  // tonic). For minor keys this resolves to the natural-minor V (a minor
  // triad's root pitch with the analyzer reporting a MAJOR-quality V — the
  // harmonic-minor leading-tone V chord, the canonical key-establishment chord).
  const scaleNotes = ScaleLib.getNotes({
    root: tonicName,
    type: minor ? 'minor' : 'major',
  })
  const root = scaleNotes[4]
  return { name: root, root }
}

/** Resolve a chord-name to its note set via the chord library. */
const chordNotesForName = (name: string): NoteName[] => {
  const variant = ChordLib.fromName(name)
  if (!variant) {return []}
  return ChordLib.getNotes(variant)
}

export const getCommonTone = (source: string, destination: string): NoteName | null => {
  const sourceTonicChord = getDiatonicChordsForKey(source)[0]
  const destinationTonicChord = getDiatonicChordsForKey(destination)[0]
  const sourceNotes = new Set(chordNotesForName(sourceTonicChord.name))
  const destinationNotes = chordNotesForName(destinationTonicChord.name)
  for (const n of destinationNotes) {
    if (sourceNotes.has(n)) {return n}
  }
  return null
}

// ─── Plan builder ─────────────────────────────────────────────────────────────

export interface ModulationPlan {
  sourceKey: string
  destinationKey: string
  technique: ModulationTechnique
  sourceProgression: Chord[]
  modulationProgression: Chord[]
  destinationProgression: Chord[]
  pivotChord?: Chord
  commonTone?: NoteName
}

/**
 * Resolve a chord name (e.g. 'C', 'Cm', 'Bdim', 'F#dim') to a ChordVariant. The
 * lookup goes through ChordLib.fromName which tries parseChordString first then
 * the all-chords library. v1 chord names are always plain triads — if a name
 * ever fails to resolve, that's a builder bug surfaced by tests, not a runtime
 * crash to swallow.
 */
const chordFromName = (name: string): ChordVariant => {
  const chord = ChordLib.fromName(name)
  if (!chord) {
    throw new Error(`modulation-lab: could not parse chord name '${name}'`)
  }
  return chord
}

const buildVamp = (key: string): Chord[] => {
  const dia = getDiatonicChordsForKey(key)
  if (isMinorKey(key)) {
    // i-♭VI-♭VII-i (natural-minor analogue per Bowie §8.7).
    const i = dia[0]
    const bVI = dia[5]
    const bVII = dia[6]
    return [chordFromName(i.name), chordFromName(bVI.name), chordFromName(bVII.name), chordFromName(i.name)]
  }
  // Major: I-V-vi-IV.
  const I = dia[0]
  const V = dia[4]
  const vi = dia[5]
  const IV = dia[3]
  return [chordFromName(I.name), chordFromName(V.name), chordFromName(vi.name), chordFromName(IV.name)]
}

const buildPivotPhase = (source: string, destination: string): { phase: Chord[]; pivot: Chord } => {
  const pivot = selectPivotChord(source, destination)
  if (!pivot) {
    throw new Error(`modulation-lab: no pivot chord between ${source} and ${destination}`)
  }
  const dom = getDominantOfKey(destination)
  const destTonic = getDiatonicChordsForKey(destination)[0]
  // [pivot, pivot, V/dest, I/dest]
  return {
    phase: [
      chordFromName(pivot.name),
      chordFromName(pivot.name),
      chordFromName(dom.name),
      chordFromName(destTonic.name),
    ],
    pivot: chordFromName(pivot.name),
  }
}

const buildDirectPhase = (source: string, destination: string): Chord[] => {
  const sourceTonic = getDiatonicChordsForKey(source)[0]
  const dom = getDominantOfKey(destination)
  const destTonic = getDiatonicChordsForKey(destination)[0]
  // [I/source, V/dest, V/dest, I/dest] — the V/dest appears abruptly after the
  // source tonic, then re-asserts before resolving.
  return [
    chordFromName(sourceTonic.name),
    chordFromName(dom.name),
    chordFromName(dom.name),
    chordFromName(destTonic.name),
  ]
}

const buildCommonTonePhase = (
  source: string,
  destination: string
): { phase: Chord[]; commonTone: NoteName } => {
  const ct = getCommonTone(source, destination)
  if (!ct) {
    throw new Error(`modulation-lab: no common tone between ${source} and ${destination}`)
  }
  const sourceTonic = getDiatonicChordsForKey(source)[0]
  const destTonic = getDiatonicChordsForKey(destination)[0]
  // Bridge 1: source-diatonic chord (not I/source) that contains the CT.
  const sourceDia = getDiatonicChordsForKey(source).slice(1)
  const bridge1 = sourceDia.find(c => chordNotesForName(c.name).includes(ct)) ?? sourceDia[0]
  // Bridge 2: dest-diatonic chord (not I/dest) that contains the CT.
  const destDia = getDiatonicChordsForKey(destination).slice(1)
  const bridge2 = destDia.find(c => chordNotesForName(c.name).includes(ct)) ?? destDia[0]
  // [I/source, bridge1, bridge2, I/dest] — every chord contains the CT (the two
  // tonics by definition, the bridges by selection).
  return {
    phase: [
      chordFromName(sourceTonic.name),
      chordFromName(bridge1.name),
      chordFromName(bridge2.name),
      chordFromName(destTonic.name),
    ],
    commonTone: ct,
  }
}

const buildParallelPhase = (source: string): Chord[] => {
  // [I/source major, I/source minor, iv/source minor, i/source minor] — for
  // source='C': [C, Cm, Fm, Cm]. The major→minor pivot is the audible
  // 3rd-degree shift (E → E♭).
  const major = chordFromName(source)
  const minor = chordFromName(`${source}m`)
  // iv of source minor = 4th scale degree of source's parallel minor (spelling
  // delegated to ScaleLib so flat keys stay flat-side).
  const parallelMinorScale = ScaleLib.getNotes({ root: source as NoteName, type: 'minor' })
  const ivRoot = parallelMinorScale[3]
  const ivChord = chordFromName(`${ivRoot}m`)
  return [major, minor, ivChord, minor]
}

export const buildModulationPlan = (input: {
  sourceKey: string
  destinationKey: string
  technique: ModulationTechnique
}): ModulationPlan => {
  const { sourceKey, destinationKey, technique } = input
  const sourceProgression = buildVamp(sourceKey)
  const destinationProgression = buildVamp(destinationKey)

  if (technique === 'pivot') {
    const { phase, pivot } = buildPivotPhase(sourceKey, destinationKey)
    return {
      sourceKey,
      destinationKey,
      technique,
      sourceProgression,
      modulationProgression: phase,
      destinationProgression,
      pivotChord: pivot,
    }
  }
  if (technique === 'direct') {
    return {
      sourceKey,
      destinationKey,
      technique,
      sourceProgression,
      modulationProgression: buildDirectPhase(sourceKey, destinationKey),
      destinationProgression,
    }
  }
  if (technique === 'common-tone') {
    const { phase, commonTone } = buildCommonTonePhase(sourceKey, destinationKey)
    return {
      sourceKey,
      destinationKey,
      technique,
      sourceProgression,
      modulationProgression: phase,
      destinationProgression,
      commonTone,
    }
  }
  // parallel
  return {
    sourceKey,
    destinationKey,
    technique,
    sourceProgression,
    modulationProgression: buildParallelPhase(sourceKey),
    destinationProgression,
  }
}

// ─── Quiz machinery ───────────────────────────────────────────────────────────

export interface ModulationQuizQuestion {
  plan: ModulationPlan
  acceptedAnswer: ModulationTechnique
}

export interface ModulationQuizVerdict {
  correct: boolean
  picked: ModulationTechnique
  question: ModulationQuizQuestion
}

export interface ModulationQuizResult {
  question: ModulationQuizQuestion
  verdict: ModulationQuizVerdict
}

export const generateModulationQuizQuestion = (
  rng: () => number = Math.random
): ModulationQuizQuestion => {
  const sourceIdx = Math.min(SOURCE_KEY_POOL.length - 1, Math.floor(rng() * SOURCE_KEY_POOL.length))
  const sourceKey = SOURCE_KEY_POOL[sourceIdx]
  const techniqueIdx = Math.min(
    MODULATION_TECHNIQUES.length - 1,
    Math.floor(rng() * MODULATION_TECHNIQUES.length)
  )
  const technique = MODULATION_TECHNIQUES[techniqueIdx]
  const destinations = getValidDestinations(sourceKey, technique)
  const destIdx = Math.min(destinations.length - 1, Math.floor(rng() * destinations.length))
  const destinationKey = destinations[destIdx]
  const plan = buildModulationPlan({ sourceKey, destinationKey, technique })
  return { plan, acceptedAnswer: technique }
}

export const validateModulationQuizAnswer = (
  question: ModulationQuizQuestion,
  picked: ModulationTechnique
): ModulationQuizVerdict => ({
  correct: picked === question.acceptedAnswer,
  picked,
  question,
})

export interface ModulationSessionStats {
  total: number
  correct: number
  byTechnique: Record<ModulationTechnique, { total: number; correct: number }>
}

export const computeModulationSessionStats = (
  results: readonly ModulationQuizResult[]
): ModulationSessionStats => {
  const stats: ModulationSessionStats = {
    total: results.length,
    correct: results.filter(r => r.verdict.correct).length,
    byTechnique: {
      direct: { total: 0, correct: 0 },
      pivot: { total: 0, correct: 0 },
      'common-tone': { total: 0, correct: 0 },
      parallel: { total: 0, correct: 0 },
    },
  }
  for (const r of results) {
    const bucket = stats.byTechnique[r.question.acceptedAnswer]
    bucket.total += 1
    if (r.verdict.correct) {bucket.correct += 1}
  }
  return stats
}
